import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Avatar,
  useTheme,
} from "@mui/material";
import { Tables } from "../../../database.types";
import { App } from "../../types/types";
import { QuestionAnswerRow } from "../../data/gameService";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Link } from "react-router-dom";
import { getTeamScoresDB, isLifelineUsedDB } from "../../utils/utils";
import { ControlCenterRoundContainer } from "./ControlCenterComponents";
import { Close } from "@mui/icons-material";
import { StandardButton } from "../../components/StandardButton";
import { gameService } from "../../data/gameService";
import { useSupabase } from "../../auth/useSupabase";
import { useNotification } from "../../contexts/NotificationContext";
import { useState } from "react";

type LifelineDisplayData = {
  isActive: boolean;
  isDisabled: boolean;
  roundUsed: number | null;
};

type LifelineDisplay = {
  phoneAFriend: LifelineDisplayData;
  doubleUp: LifelineDisplayData;
  doubleSip: LifelineDisplayData;
};

interface StandardRoundProps {
  game: Tables<"games">;
  gameState: Tables<"game_state">;
  rounds: Tables<"rounds">[];
  questions: Tables<"questions">[];
  lifelines: Tables<"round_lifelines">[];
  bonusRounds: Tables<"bonus_rounds">[];
  classicBonusAnswers: Tables<"classic_bonus_answers">[];
  classicBonusTeamResponses: Tables<"classic_bonus_team_responses">[];
  closestGuessQuestions: Tables<"closest_guess_questions">[];
  team1: App.TeamWithMembers;
  team2: App.TeamWithMembers;
  gameId: string;
  questionAnswers: QuestionAnswerRow[];
  refetchData: () => Promise<void>;
}

const StandardRound = (props: StandardRoundProps) => {
  const {
    game,
    gameState,
    rounds,
    questions,
    lifelines,
    bonusRounds,
    classicBonusAnswers,
    classicBonusTeamResponses,
    closestGuessQuestions,
    team1,
    team2,
    gameId,
    questionAnswers,
    refetchData,
  } = props;

  const theme = useTheme();
  const { supabase } = useSupabase();
  const { withErrorNotification } = useNotification();

  const [awardPointLoading, setAwardPointLoading] = useState<boolean>(false);
  const [changeQuestionLoading, setChangeQuestionLoading] =
    useState<boolean>(false);
  const [showQuestionLoading, setShowQuestionLoading] =
    useState<boolean>(false);
  const [showAnswerLoading, setShowAnswerLoading] = useState<boolean>(false);
  const [wrongLoading, setWrongLoading] = useState<boolean>(false);
  const [lifelineLoading, setLifelineLoading] = useState<string | null>(null);
  const [declareWinnerLoading, setDeclareWinnerLoading] =
    useState<boolean>(false);

  const currentRoundNumber = gameState.current_round_number;
  const currentRound = rounds.find(
    (r) => r.round_number === currentRoundNumber,
  );

  if (!currentRound || currentRoundNumber === null) {
    return null;
  }

  // 0-indexed for ControlCenterRoundContainer display
  const currentRoundIdx = currentRoundNumber - 1;

  const questionNumber = gameState.current_question_num;
  const currentQuestion = questions.find(
    (q) =>
      q.round_id === currentRound.id && q.question_number === questionNumber,
  );
  const currentRoundLifelines = lifelines.filter(
    (l) => l.round_id === currentRound.id,
  );
  const currentQuestionAnswers = currentQuestion
    ? questionAnswers.filter((a) => a.question_id === currentQuestion.id)
    : [];

  const onAwardPoint = withErrorNotification(async (teamId: string) => {
    if (!currentQuestion) return;
    const correctAudio = document.getElementById(
      "correct-audio",
    ) as HTMLAudioElement;
    if (correctAudio) {
      correctAudio.play();
    }
    // Clear existing point scorers when switching teams
    if (
      currentQuestion.correct_team_id &&
      currentQuestion.correct_team_id !== teamId
    ) {
      await gameService.clearQuestionAnswers(supabase, currentQuestion.id);
    }
    await gameService.updateQuestion(supabase, currentQuestion.id, {
      correct_team_id: teamId,
    });
    await gameService.toggleShowAnswer(supabase, gameId, true);
    await refetchData();
  });

  const onUnawardPoint = withErrorNotification(async () => {
    if (!currentQuestion) return;
    await gameService.clearQuestionAnswers(supabase, currentQuestion.id);
    await gameService.updateQuestion(supabase, currentQuestion.id, {
      correct_team_id: null,
    });
    await refetchData();
  });

  const onTogglePointScorer = withErrorNotification(
    async (profileId: string) => {
      if (!currentQuestion) return;
      const isSelected = currentQuestionAnswers.some(
        (a) => a.profile_id === profileId,
      );
      if (isSelected) {
        await gameService.removeQuestionAnswer(
          supabase,
          currentQuestion.id,
          profileId,
        );
      } else {
        await gameService.addQuestionAnswer(
          supabase,
          currentQuestion.id,
          profileId,
        );
      }
      await refetchData();
    },
  );

  const onChangeQuestion = withErrorNotification(
    async (questionNum: number) => {
      await gameService.switchQuestion(supabase, gameId, questionNum);
      await refetchData();
    },
  );

  const onClickLifeline = withErrorNotification(
    async (teamId: string, lifelineType: string) => {
      const isActive = currentRoundLifelines.some(
        (l) => l.team_id === teamId && l.lifeline_type === lifelineType,
      );
      if (isActive) {
        await gameService.removeLifelineByType(
          supabase,
          currentRound.id,
          teamId,
          lifelineType,
        );
      } else {
        await gameService.addLifeline(supabase, {
          round_id: currentRound.id,
          team_id: teamId,
          lifeline_type: lifelineType,
        });
      }
      await refetchData();
    },
  );

  const isTeamCorrect = (teamId: string) => {
    return currentQuestion?.correct_team_id === teamId;
  };

  const getRoundUsed = (
    teamId: string,
    lifelineType: string,
  ): number | null => {
    const lifeline = lifelines.find(
      (l) => l.team_id === teamId && l.lifeline_type === lifelineType,
    );
    if (!lifeline) return null;
    const round = rounds.find((r) => r.id === lifeline.round_id);
    return round ? round.round_number : null;
  };

  const getLifelineDisplay = (
    teamId: string,
    isTeam1: boolean,
  ): LifelineDisplay => {
    const isQ1 = questionNumber === 1;
    const isQ2 = questionNumber === 2;
    // Phone a Friend and Double Sip are only for the team's own question
    // Team 1 answers Q1, Team 2 answers Q2
    const wrongQuestionForLifeline = isTeam1 ? isQ2 : isQ1;

    const makeLifelineData = (
      lifelineType: string,
      restrictToOwnQuestion: boolean,
    ): LifelineDisplayData => {
      const isActive = currentRoundLifelines.some(
        (l) => l.team_id === teamId && l.lifeline_type === lifelineType,
      );
      const usedGlobally = isLifelineUsedDB(lifelines, teamId, lifelineType);
      const isDisabled =
        (usedGlobally && !isActive) ||
        (restrictToOwnQuestion && wrongQuestionForLifeline);
      return {
        isActive,
        isDisabled,
        roundUsed: getRoundUsed(teamId, lifelineType),
      };
    };

    return {
      phoneAFriend: makeLifelineData("Phone a Friend", true),
      doubleSip: makeLifelineData("Double Sip", true),
      doubleUp: makeLifelineData("Double up", false),
    };
  };

  const team1LifelineDisplayObj = getLifelineDisplay(team1.id, true);
  const team2LifelineDisplayObj = getLifelineDisplay(team2.id, false);

  const isTeam1Correct = isTeamCorrect(team1.id);
  const isTeam2Correct = isTeamCorrect(team2.id);

  const getCategoryTitle = () => {
    if (currentRoundNumber === 10) {
      return questionNumber === 1
        ? game.team_1_niche_category
        : game.team_2_niche_category;
    }
    return currentRound.category;
  };

  const teamPoints = getTeamScoresDB(
    rounds,
    questions,
    lifelines,
    bonusRounds,
    classicBonusAnswers,
    classicBonusTeamResponses,
    closestGuessQuestions,
    team1.id,
    team2.id,
  );

  const onClickPrevious = withErrorNotification(async () => {
    if (currentRoundNumber === 1) {
      // Go to not-started
      await gameService.navigateToRound(supabase, gameId, null, "not_started");
    } else if (currentRoundNumber === 7) {
      // Go to bonus
      await gameService.navigateToBonusRound(supabase, gameId);
    } else {
      await gameService.navigateToRound(
        supabase,
        gameId,
        currentRoundNumber - 1,
      );
    }
    await refetchData();
  });

  const onClickNext = withErrorNotification(async () => {
    if (currentRoundNumber === 6) {
      // Go to bonus
      await gameService.navigateToBonusRound(supabase, gameId);
    } else if (currentRoundNumber === 12) {
      // Go to overtime
      await gameService.navigateToOvertime(supabase, gameId);
    } else {
      await gameService.navigateToRound(
        supabase,
        gameId,
        currentRoundNumber + 1,
      );
    }
    await refetchData();
  });

  const onDeclareWinner = withErrorNotification(
    async (winningTeamId: string | null) => {
      await gameService.update(supabase, gameId, {
        winning_team_id: winningTeamId,
      });
      await refetchData();
    },
  );

  const onToggleShowQuestion = withErrorNotification(async () => {
    await gameService.toggleShowQuestion(
      supabase,
      gameId,
      !gameState.show_question,
    );
    await refetchData();
  });

  const onToggleShowAnswer = withErrorNotification(async () => {
    await gameService.toggleShowAnswer(
      supabase,
      gameId,
      !gameState.show_answer,
    );
    await refetchData();
  });

  const onMarkWrong = withErrorNotification(async () => {
    await gameService.setSteal(supabase, gameId, true);
    await refetchData();
  });

  return (
    <ControlCenterRoundContainer
      currentRoundIdx={currentRoundIdx}
      category={getCategoryTitle() ?? undefined}
      onClickPrevious={onClickPrevious}
      onClickNext={onClickNext}
    >
      <Box
        sx={{
          display: "flex",
          width: "100%",
          maxWidth: "800px",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {/* Question Controls */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            border: "1px solid",
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.dark,
          }}
        >
          {/* Question Selector */}
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <StandardButton
              variant={questionNumber === 1 ? "contained" : "outlined"}
              onClick={async () => {
                setChangeQuestionLoading(true);
                await onChangeQuestion(1);
                setChangeQuestionLoading(false);
              }}
              loading={changeQuestionLoading}
              fullWidth
              size="large"
            >
              Question 1
            </StandardButton>
            <StandardButton
              variant={questionNumber === 2 ? "contained" : "outlined"}
              onClick={async () => {
                setChangeQuestionLoading(true);
                await onChangeQuestion(2);
                setChangeQuestionLoading(false);
              }}
              loading={changeQuestionLoading}
              fullWidth
              size="large"
            >
              Question 2
            </StandardButton>
          </Box>

          {/* Visibility Controls */}
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <StandardButton
              variant={gameState.show_question ? "contained" : "outlined"}
              onClick={async () => {
                setShowQuestionLoading(true);
                await onToggleShowQuestion();
                setShowQuestionLoading(false);
              }}
              loading={showQuestionLoading}
              startIcon={
                gameState.show_question ? (
                  <VisibilityOffIcon />
                ) : (
                  <VisibilityIcon />
                )
              }
              fullWidth
              size="large"
            >
              {gameState.show_question ? "Hide Question" : "Show Question"}
            </StandardButton>
            <StandardButton
              variant={gameState.show_answer ? "contained" : "outlined"}
              onClick={async () => {
                setShowAnswerLoading(true);
                await onToggleShowAnswer();
                setShowAnswerLoading(false);
              }}
              loading={showAnswerLoading}
              startIcon={
                gameState.show_answer ? (
                  <VisibilityOffIcon />
                ) : (
                  <VisibilityIcon />
                )
              }
              fullWidth
              size="large"
              color="success"
            >
              {gameState.show_answer ? "Hide Answer" : "Show Answer"}
            </StandardButton>
          </Box>

          {/* Question/Answer Display */}
          {gameState.show_question && currentQuestion && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: theme.palette.primary.main,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "rgba(100, 116, 139, 0.15)",
                }}
              >
                <Typography variant="h6" color="text.primary">
                  {currentQuestion.question_text}
                </Typography>
              </Paper>

              {gameState.show_answer && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: "rgba(5, 150, 105, 0.08)",
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: "success.main",
                  }}
                >
                  <Typography
                    variant="h6"
                    color="success.dark"
                    fontWeight="600"
                  >
                    {currentQuestion.answer_text}
                  </Typography>
                </Paper>
              )}
            </Box>
          )}
        </Paper>

        {/* Teams Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
          }}
        >
          {/* Team One */}
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              flexDirection: "column",
              p: 2,
              borderRadius: 3,
              border: "2px solid",
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.primary.dark,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.06)",
              },
              gap: 1,
            }}
          >
            {/* Team Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h6" fontWeight="600" color="primary">
                {team1.name}
              </Typography>

              <Avatar
                sx={{
                  bgcolor: "primary.main",
                  width: 48,
                  height: 48,
                  fontSize: "1.25rem",
                  fontWeight: "700",
                }}
              >
                {teamPoints.team1}
              </Avatar>
            </Box>

            {/* Answer Controls */}
            <Box mb={1}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Mark Answer
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <StandardButton
                  variant={isTeam1Correct ? "contained" : "outlined"}
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={async () => {
                    setAwardPointLoading(true);
                    if (isTeam1Correct) {
                      await onUnawardPoint();
                    } else {
                      await onAwardPoint(team1.id);
                    }
                    setAwardPointLoading(false);
                  }}
                  loading={awardPointLoading}
                  size="large"
                  disabled={questionNumber === 2 && !gameState.is_steal}
                  fullWidth
                >
                  Correct
                </StandardButton>
                <StandardButton
                  variant="outlined"
                  color="error"
                  startIcon={<Close />}
                  onClick={async () => {
                    setWrongLoading(true);
                    await onMarkWrong();
                    setWrongLoading(false);
                  }}
                  loading={wrongLoading}
                  disabled={questionNumber === 2}
                  size="large"
                  fullWidth
                >
                  Wrong
                </StandardButton>
              </Box>
            </Box>

            {/* Point Scorers */}
            {isTeam1Correct && team1.members.length > 0 && (
              <Box mb="12px">
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Point Scorers
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {team1.members.map((member) => {
                    const isSelected = currentQuestionAnswers.some(
                      (a) => a.profile_id === member.id,
                    );
                    return (
                      <Chip
                        key={member.id}
                        label={member.name}
                        variant={isSelected ? "filled" : "outlined"}
                        color={isSelected ? "success" : "default"}
                        onClick={() => onTogglePointScorer(member.id)}
                        sx={{ cursor: "pointer" }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Lifelines */}
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Lifelines
              </Typography>
              <Stack spacing={1}>
                <StandardButton
                  variant={
                    team1LifelineDisplayObj.doubleSip.isActive ||
                    team1LifelineDisplayObj.doubleSip.isDisabled
                      ? "contained"
                      : "outlined"
                  }
                  color="primary"
                  disabled={team1LifelineDisplayObj.doubleSip.isDisabled}
                  onClick={async () => {
                    setLifelineLoading(`${team1.id}-Double Sip`);
                    await onClickLifeline(team1.id, "Double Sip");
                    setLifelineLoading(null);
                  }}
                  loading={lifelineLoading === `${team1.id}-Double Sip`}
                  fullWidth
                  sx={{
                    justifyContent: "space-between",
                    opacity: team1LifelineDisplayObj.doubleSip.isDisabled
                      ? 0.5
                      : 1,
                    ":disabled": {
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <span>Double Sip</span>
                  {team1LifelineDisplayObj.doubleSip.roundUsed !== null && (
                    <Chip
                      label={`Rd ${team1LifelineDisplayObj.doubleSip.roundUsed}`}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </StandardButton>
                <StandardButton
                  variant={
                    team1LifelineDisplayObj.phoneAFriend.isActive ||
                    team1LifelineDisplayObj.phoneAFriend.isDisabled
                      ? "contained"
                      : "outlined"
                  }
                  color="primary"
                  disabled={team1LifelineDisplayObj.phoneAFriend.isDisabled}
                  onClick={async () => {
                    setLifelineLoading(`${team1.id}-Phone a Friend`);
                    await onClickLifeline(team1.id, "Phone a Friend");
                    setLifelineLoading(null);
                  }}
                  loading={lifelineLoading === `${team1.id}-Phone a Friend`}
                  fullWidth
                  sx={{
                    justifyContent: "space-between",
                    opacity: team1LifelineDisplayObj.phoneAFriend.isDisabled
                      ? 0.5
                      : 1,
                    ":disabled": {
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <span>Phone a Friend</span>
                  {team1LifelineDisplayObj.phoneAFriend.roundUsed !== null && (
                    <Chip
                      label={`Rd ${team1LifelineDisplayObj.phoneAFriend.roundUsed}`}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </StandardButton>
                <StandardButton
                  variant={
                    team1LifelineDisplayObj.doubleUp.isActive ||
                    team1LifelineDisplayObj.doubleUp.isDisabled
                      ? "contained"
                      : "outlined"
                  }
                  color="primary"
                  disabled={team1LifelineDisplayObj.doubleUp.isDisabled}
                  onClick={async () => {
                    setLifelineLoading(`${team1.id}-Double up`);
                    await onClickLifeline(team1.id, "Double up");
                    setLifelineLoading(null);
                  }}
                  loading={lifelineLoading === `${team1.id}-Double up`}
                  fullWidth
                  sx={{
                    justifyContent: "space-between",
                    opacity: team1LifelineDisplayObj.doubleUp.isDisabled
                      ? 0.5
                      : 1,
                    ":disabled": {
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <span>Double Up</span>
                  {team1LifelineDisplayObj.doubleUp.roundUsed !== null && (
                    <Chip
                      label={`Rd ${team1LifelineDisplayObj.doubleUp.roundUsed}`}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </StandardButton>
              </Stack>
            </Box>
          </Paper>

          {/* Team Two */}
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              flexDirection: "column",
              p: 2,
              borderRadius: 3,
              border: "2px solid",
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.primary.dark,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.06)",
              },
              gap: 1,
            }}
          >
            {/* Team Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "primary.main",
                  width: 48,
                  height: 48,
                  fontSize: "1.25rem",
                  fontWeight: "700",
                }}
              >
                {teamPoints.team2}
              </Avatar>

              <Typography variant="h6" fontWeight="600" color="primary">
                {team2.name}
              </Typography>
            </Box>

            {/* Answer Controls */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Mark Answer
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <StandardButton
                  variant={isTeam2Correct ? "contained" : "outlined"}
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={async () => {
                    setAwardPointLoading(true);
                    if (isTeam2Correct) {
                      await onUnawardPoint();
                    } else {
                      await onAwardPoint(team2.id);
                    }
                    setAwardPointLoading(false);
                  }}
                  loading={awardPointLoading}
                  size="large"
                  disabled={questionNumber === 1 && !gameState.is_steal}
                  fullWidth
                >
                  Correct
                </StandardButton>
                <StandardButton
                  variant="outlined"
                  color="error"
                  startIcon={<Close />}
                  onClick={async () => {
                    setWrongLoading(true);
                    await onMarkWrong();
                    setWrongLoading(false);
                  }}
                  loading={wrongLoading}
                  disabled={questionNumber === 1}
                  size="large"
                  fullWidth
                >
                  Wrong
                </StandardButton>
              </Box>
            </Box>

            {/* Point Scorers */}
            {isTeam2Correct && team2.members.length > 0 && (
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Point Scorers
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {team2.members.map((member) => {
                    const isSelected = currentQuestionAnswers.some(
                      (a) => a.profile_id === member.id,
                    );
                    return (
                      <Chip
                        key={member.id}
                        label={member.name}
                        variant={isSelected ? "filled" : "outlined"}
                        color={isSelected ? "success" : "default"}
                        onClick={() => onTogglePointScorer(member.id)}
                        sx={{ cursor: "pointer" }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Lifelines */}
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Lifelines
              </Typography>
              <Stack spacing={1}>
                <StandardButton
                  variant={
                    team2LifelineDisplayObj.doubleSip.isActive ||
                    team2LifelineDisplayObj.doubleSip.isDisabled
                      ? "outlined"
                      : "contained"
                  }
                  color="secondary"
                  disabled={team2LifelineDisplayObj.doubleSip.isDisabled}
                  onClick={async () => {
                    setLifelineLoading(`${team2.id}-Double Sip`);
                    await onClickLifeline(team2.id, "Double Sip");
                    setLifelineLoading(null);
                  }}
                  loading={lifelineLoading === `${team2.id}-Double Sip`}
                  fullWidth
                  sx={{
                    justifyContent: "space-between",
                    opacity: team2LifelineDisplayObj.doubleSip.isDisabled
                      ? 0.5
                      : 1,
                    ":disabled": {
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <span>Double Sip</span>
                  {team2LifelineDisplayObj.doubleSip.roundUsed !== null && (
                    <Chip
                      label={`Rd ${team2LifelineDisplayObj.doubleSip.roundUsed}`}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </StandardButton>
                <StandardButton
                  variant={
                    team2LifelineDisplayObj.phoneAFriend.isActive ||
                    team2LifelineDisplayObj.phoneAFriend.isDisabled
                      ? "contained"
                      : "outlined"
                  }
                  color="secondary"
                  disabled={team2LifelineDisplayObj.phoneAFriend.isDisabled}
                  onClick={async () => {
                    setLifelineLoading(`${team2.id}-Phone a Friend`);
                    await onClickLifeline(team2.id, "Phone a Friend");
                    setLifelineLoading(null);
                  }}
                  loading={lifelineLoading === `${team2.id}-Phone a Friend`}
                  fullWidth
                  sx={{
                    justifyContent: "space-between",
                    opacity: team2LifelineDisplayObj.phoneAFriend.isDisabled
                      ? 0.5
                      : 1,
                    ":disabled": {
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <span>Phone a Friend</span>
                  {team2LifelineDisplayObj.phoneAFriend.roundUsed !== null && (
                    <Chip
                      label={`Rd ${team2LifelineDisplayObj.phoneAFriend.roundUsed}`}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </StandardButton>
                <StandardButton
                  variant={
                    team2LifelineDisplayObj.doubleUp.isActive ||
                    team2LifelineDisplayObj.doubleUp.isDisabled
                      ? "contained"
                      : "outlined"
                  }
                  color="secondary"
                  disabled={team2LifelineDisplayObj.doubleUp.isDisabled}
                  onClick={async () => {
                    setLifelineLoading(`${team2.id}-Double up`);
                    await onClickLifeline(team2.id, "Double up");
                    setLifelineLoading(null);
                  }}
                  loading={lifelineLoading === `${team2.id}-Double up`}
                  fullWidth
                  sx={{
                    justifyContent: "space-between",
                    opacity: team2LifelineDisplayObj.doubleUp.isDisabled
                      ? 0.5
                      : 1,
                    ":disabled": {
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <span>Double Up</span>
                  {team2LifelineDisplayObj.doubleUp.roundUsed !== null && (
                    <Chip
                      label={`Rd ${team2LifelineDisplayObj.doubleUp.roundUsed}`}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </StandardButton>
              </Stack>
            </Box>
          </Paper>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 250 }}>
            <Link
              to={`/game/${gameId}`}
              target="_blank"
              style={{ textDecoration: "none", display: "block" }}
            >
              <StandardButton
                variant="contained"
                fullWidth
                size="large"
                color="info"
              >
                Open Participant View
              </StandardButton>
            </Link>
          </Box>
          {currentRoundIdx === 11 && (
            <Box sx={{ flex: 1, minWidth: 250 }}>
              <StandardButton
                variant="contained"
                fullWidth
                size="large"
                disabled={
                  teamPoints.team1 === teamPoints.team2 && !game.winning_team_id
                }
                onClick={async () => {
                  setDeclareWinnerLoading(true);
                  if (game.winning_team_id) {
                    await onDeclareWinner(null);
                  } else {
                    await onDeclareWinner(
                      teamPoints.team1 > teamPoints.team2 ? team1.id : team2.id,
                    );
                  }
                  setDeclareWinnerLoading(false);
                }}
                loading={declareWinnerLoading}
              >
                {`${game.winning_team_id ? "Un-" : ""}Declare ${
                  teamPoints.team1 > teamPoints.team2 ? team1.name : team2.name
                } Winner`}
              </StandardButton>
            </Box>
          )}
        </Box>
      </Box>
      <audio src="/sounds/wrong-answer.mp3" id="buzzer-audio" />
      <audio src="/sounds/correct-answer.mp3" id="correct-audio" />
    </ControlCenterRoundContainer>
  );
};

export default StandardRound;
